import { apiClient } from '../utils/apiClient'
import { LOGIN_ATTRIBUTION_LS_KEY, MAX_LOGIN_ATTRIBUTION_LEN } from '../services/loginAttribution'

export type IdentityProvider = 'github' | 'google'

export type LinkedIdentity = {
  provider: string
  username?: string
  email?: string
}

const START_PATH: Record<IdentityProvider, string> = {
  github: '/api/auth/github',
  google: '/api/auth/login/google',
}

function isDevConsoleHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === 'app.veilstreamdev.com' ||
    host.endsWith('.veilstreamdev.com')
  )
}

export function shouldShowGoogleLogin(providers: AuthProviderInfo[]): boolean {
  if (providers.some((p) => p.name === 'google' && p.configured)) return true
  return isDevConsoleHost()
}

export async function initiateOAuth(provider: IdentityProvider = 'github'): Promise<void> {
  const redirectURL = window.location.origin + window.location.pathname + window.location.search
  const qs = new URLSearchParams()
  qs.set('redirect_url', redirectURL)
  const pending = localStorage.getItem(LOGIN_ATTRIBUTION_LS_KEY)?.trim()
  if (pending) {
    qs.set('login_attribution_code', pending.slice(0, MAX_LOGIN_ATTRIBUTION_LEN))
  }

  const data = await apiClient.get<{ auth_url: string }>(`${START_PATH[provider]}?${qs.toString()}`)
  if (!data.auth_url) {
    throw new Error('No auth URL in response')
  }
  window.location.href = data.auth_url
}

export async function initiateOAuthLink(provider: IdentityProvider): Promise<void> {
  const redirectURL = `${window.location.origin}/settings`
  const qs = new URLSearchParams()
  qs.set('redirect_url', redirectURL)
  const data = await apiClient.get<{ auth_url: string }>(`/api/auth/link/${provider}?${qs.toString()}`, undefined, {
    skipOrganizationHeader: true,
  })
  if (!data.auth_url) {
    throw new Error('No auth URL in response')
  }
  window.location.href = data.auth_url
}

export async function unlinkIdentity(provider: IdentityProvider): Promise<void> {
  await apiClient.request(`/api/user/identities/${provider}`, {
    method: 'DELETE',
    skipOrganizationHeader: true,
  })
}

export type AuthProviderInfo = { name: string; configured: boolean }

export async function fetchAuthProviders(): Promise<AuthProviderInfo[]> {
  try {
    const data = await apiClient.get<{ providers?: AuthProviderInfo[] }>('/api/auth/providers')
    return data.providers ?? []
  } catch {
    return [{ name: 'github', configured: true }]
  }
}

export function oauthErrorMessage(errorCode: string, description?: string | null): string {
  if (description?.trim()) return description.trim()
  const errorMessages: Record<string, string> = {
    access_denied: 'You denied the authorization request. Please try again.',
    invalid_scope: 'The requested permissions are invalid. Please try again.',
    server_error: 'GitHub server error. Please try again later.',
    temporarily_unavailable: 'GitHub service is temporarily unavailable. Please try again later.',
    auth_expired: 'Authentication expired. Please sign in again.',
    invalid_state: 'Authentication session expired or invalid. Please try again.',
    token_exchange_failed: 'Failed to authenticate. Please try again.',
    user_info_failed: 'Failed to retrieve user information. Please try again.',
    database_error: 'Failed to save user data. Please try again.',
    session_creation_failed: 'Failed to create user session. Please try again.',
    email_unverified: 'Your Google account email must be verified to sign in.',
    identity_in_use: 'That account is already connected to another VeilStream user.',
    already_connected: 'You already have that sign-in method connected. Disconnect it first to use a different account.',
  }
  return errorMessages[errorCode] || 'An unexpected error occurred during authentication. Please try again.'
}
